// Sources, citations, media, and to-dos: lookups and edits that cut across people and
// relationships. Citations live on what they support (a person, one of their events, or a
// relationship), so finding "everything this source backs up" means walking the tree.

import { dateSortKey } from './dates';
import type { Citation, Id, MediaItem, Person, Relationship, Source, Todo, TodoPriority, Tree } from './types';

/** What a citation is attached to. */
export type CiteTarget = { personId: Id; eventId?: Id } | { relId: Id };

export interface CitationRef {
  target: CiteTarget;
  citation: Citation;
  /** Position in the target's citation list. */
  index: number;
}

export function citationsAt(tree: Tree, target: CiteTarget): Citation[] {
  if ('relId' in target) return tree.relationships[target.relId]?.citations ?? [];
  const p = tree.people[target.personId];
  if (!p) return [];
  if (target.eventId) return p.events.find((e) => e.id === target.eventId)?.citations ?? [];
  return p.citations ?? [];
}

/** Replace the citations on a target. An empty list removes the field. */
export function setCitations(tree: Tree, target: CiteTarget, citations: Citation[]): Tree {
  const value = citations.length ? citations : undefined;
  const now = new Date().toISOString();
  if ('relId' in target) {
    const r = tree.relationships[target.relId];
    if (!r) return tree;
    return { ...tree, updatedAt: now, relationships: { ...tree.relationships, [r.id]: { ...r, citations: value } as Relationship } };
  }
  const p = tree.people[target.personId];
  if (!p) return tree;
  const next: Person = target.eventId
    ? { ...p, events: p.events.map((e) => (e.id === target.eventId ? { ...e, citations: value } : e)) }
    : { ...p, citations: value };
  return { ...tree, updatedAt: now, people: { ...tree.people, [p.id]: next } };
}

/** Every citation about a person: on them, their events, and their relationships. */
export function citationsAbout(tree: Tree, personId: Id): CitationRef[] {
  const p = tree.people[personId];
  if (!p) return [];
  const out: CitationRef[] = [];
  (p.citations ?? []).forEach((citation, index) => out.push({ target: { personId }, citation, index }));
  const events = [...p.events].sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date));
  for (const e of events) (e.citations ?? []).forEach((citation, index) => out.push({ target: { personId, eventId: e.id }, citation, index }));
  for (const r of Object.values(tree.relationships)) {
    if (r.a !== personId && r.b !== personId) continue;
    (r.citations ?? []).forEach((citation, index) => out.push({ target: { relId: r.id }, citation, index }));
  }
  return out;
}

/** Every place a source is cited. */
export function sourceUsage(tree: Tree, sourceId: Id): CitationRef[] {
  const out: CitationRef[] = [];
  const scan = (cs: Citation[] | undefined, target: CiteTarget) =>
    cs?.forEach((citation, index) => citation.sourceId === sourceId && out.push({ target, citation, index }));
  for (const p of Object.values(tree.people)) {
    scan(p.citations, { personId: p.id });
    for (const e of p.events) scan(e.citations, { personId: p.id, eventId: e.id });
  }
  for (const r of Object.values(tree.relationships)) scan(r.citations, { relId: r.id });
  return out;
}

/** How many times each source is cited, for lists. */
export function citationCounts(tree: Tree): Map<Id, number> {
  const counts = new Map<Id, number>();
  const add = (cs: Citation[] | undefined) => cs?.forEach((c) => counts.set(c.sourceId, (counts.get(c.sourceId) ?? 0) + 1));
  for (const p of Object.values(tree.people)) {
    add(p.citations);
    for (const e of p.events) add(e.citations);
  }
  for (const r of Object.values(tree.relationships)) add(r.citations);
  return counts;
}

export function mediaOf(tree: Tree, personId: Id): MediaItem[] {
  return Object.values(tree.media)
    .filter((m) => m.people.some((x) => x.personId === personId))
    .sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date) || a.title.localeCompare(b.title));
}

export function mediaOfSource(tree: Tree, sourceId: Id): MediaItem[] {
  return Object.values(tree.media).filter((m) => m.sourceIds?.includes(sourceId));
}

const PRIORITY_ORDER: Record<TodoPriority, number> = { high: 0, normal: 1, low: 2 };

/** Open work first (high priority, then soonest due), then finished work, newest first. */
export function sortTodos(list: Todo[]): Todo[] {
  return [...list].sort((a, b) => {
    const da = a.status === 'done' ? 1 : 0;
    const db = b.status === 'done' ? 1 : 0;
    if (da !== db) return da - db;
    if (da) return (b.doneAt ?? '').localeCompare(a.doneAt ?? '');
    return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] || (a.due ?? '9999').localeCompare(b.due ?? '9999') || a.createdAt.localeCompare(b.createdAt);
  });
}

export function todosOf(tree: Tree, personId: Id): Todo[] {
  return sortTodos(Object.values(tree.todos).filter((t) => t.people.includes(personId)));
}

/** A short line for a source: "Title, Author (1963)". */
export function sourceLabel(s: Source | undefined): string {
  if (!s) return 'Missing source';
  const year = s.date?.match(/\d{3,4}/)?.[0];
  return [s.title || 'Untitled source', s.author].filter(Boolean).join(', ') + (year ? ` (${year})` : '');
}
